import React from "react";
import { useFormikContext } from "formik";

import { Select, Toggle } from "../../../components/ui";
import { useEntitlement } from "../../../contexts";
import { locationOptions } from "../../../product/constants";
import "./buy-alert-prompt.scss";

/**
 * "Watch for a supplier at this price" — offered on CONFIRM REQUEST when an
 * order is placed (Alex, 2026-10-06).
 *
 * Turning it on creates a Sales Pipeline entry with a buy target, so the daily
 * digest tells the rep the morning a trade-group quote lands at or under the
 * client's number. No listing is involved: the entry exists to answer "can we
 * source this, and at what".
 *
 * ON by default for an order since 2026-10-13 (Guillaume): every sourcing
 * request belongs in the Sales Pipeline, and the rep switches it off for the
 * exception. It was opt-in until then (Alex, 2026-10-06), on the worry that
 * the digest would fill with watches nobody asked for.
 *
 * No budget still adds the reference, with no target yet; the hint says so.
 *
 * The region and keywords are the rep's own narrowing: "a Dubai seller, boxed"
 * is a different ask from "anywhere, any condition". Both are optional, and
 * the watch works without either.
 *
 * Values live in the surrounding Formik form (ConfirmRequestView) so they
 * arrive in its onSubmit alongside the rest. `saveRequest` picks its fields
 * explicitly, so these extra keys never reach the request payload.
 */
const BuyAlertPrompt = ({ currency }) => {
  const { values, setFieldValue } = useFormikContext();
  // The Sales Pipeline belongs to "Stock alert"; without it there is nowhere
  // for the watch to go.
  const hasStockAlert = useEntitlement("core_inventory_management");
  const on = !!values.watchSupplier;

  // The form's OWN currency field, falling back to the prop. The prop is
  // computed once from the request, so changing the dropdown left this line
  // saying EUR over a USD budget — the same reason V2Money reads
  // `values.currency` for its symbol.
  const activeCurrency = values.currency || currency;

  // The client's ceiling IS the watch price: anything at or under it leaves a
  // margin, anything above does not. Shown rather than asked for, so there is
  // one number in the deal rather than two that can disagree.
  //
  // Budgets are held in MINOR units, so this is divided by 100 to read as the
  // field above it does. Without that, a 2,400 budget described itself as a
  // 240,000 target — and the value POSTED was wrong too, not just this line.
  const minor = Number(values.maxBudget || values.priceExpectation || 0);
  const target = minor > 0 ? Math.round(minor / 100) : 0;

  if (!hasStockAlert) return null;

  return (
    <div className={`buy-alert-prompt${on ? " is-on" : ""}`}>
      <div className="buy-alert-prompt__head">
        {/* aria-label, not `label`: Toggle renders its own visible label, and
            the copy below already carries the wording plus the explanation.
            Passing both would print the sentence twice and read it twice. */}
        <Toggle
          checked={on}
          onChange={(next) => setFieldValue("watchSupplier", next)}
          aria-label="Watch for a supplier at this price"
        />
        <div className="buy-alert-prompt__copy">
          <span className="buy-alert-prompt__title">
            Watch for a supplier at this price
          </span>
          <span className="buy-alert-prompt__hint">
            {target
              ? `Adds this reference to Sales Pipeline with a ${String(
                  activeCurrency || ""
                ).toUpperCase()} ${target.toLocaleString()} target. You'll be told the morning a trade-group quote lands at or under it.`
              : "Adds this reference to Sales Pipeline. Add a budget to set a price target."}
          </span>
        </div>
      </div>

      {on && (
        <div className="buy-alert-prompt__fields">
          <div className="buy-alert-prompt__field">
            <span>Region</span>
            {/* "" = Anywhere: ui/Select shows it as the placeholder. */}
            <Select
              fullWidth
              label="Region"
              placeholder="Anywhere"
              options={[
                { value: "", label: "Anywhere" },
                ...locationOptions.map((o) => ({ value: o.id, label: o.name })),
              ]}
              value={values.watchRegion || ""}
              onChange={(v) => setFieldValue("watchRegion", v ?? "")}
            />
          </div>

          {/* There was a free-text "Note for the buyer" here. Removed at Alex's
              request (2026-10-08): it matched nothing, and a second box beside
              a Region that DOES filter only invited the question of what it
              was for. Region is the one narrowing this prompt offers. */}
        </div>
      )}
    </div>
  );
};

export default BuyAlertPrompt;
